import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, Share } from "lucide-react";
import { isPortalPath, bootKind } from "../lib/portalPwa";

const DISMISS_KEY = "bdr_install_dismissed";
const HIDDEN_PATHS = ["/login", "/setup", "/assinar", "/verificar", "/com"];

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

function isSafari() {
  const ua = window.navigator.userAgent || "";
  return /safari/i.test(ua) && !/crios|fxios|edgios|chrome/i.test(ua);
}

export const InstallPrompt = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;
    // Pagina carregada pelo Portal do Cliente: o pedido de instalacao pertence ao app Cliente.
    if (bootKind() === "portal") return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowAndroid(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      localStorage.setItem(DISMISS_KEY, "1");
      setShowAndroid(false);
      setShowIOS(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    let iosTimer;
    if (isIOS() && isSafari()) {
      iosTimer = setTimeout(() => setShowIOS(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShowAndroid(false);
    setShowIOS(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (e) {}
    localStorage.setItem(DISMISS_KEY, "1");
    setDeferredPrompt(null);
    setShowAndroid(false);
  };

  const onHiddenPath = isPortalPath(location.pathname) || HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));
  if (onHiddenPath || (!showAndroid && !showIOS)) return null;

  return (
    <div
      data-testid="install-app-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="mx-auto max-w-md bg-white border border-[#EBE5DF] shadow-lg">
        <div className="flex items-start gap-3 p-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center bg-[#111] text-white">
            {showIOS ? <Share size={18} /> : <Download size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            {showIOS ? (
              <>
                <p className="text-sm font-medium text-[#111]">Instale o Banco das Revendedoras</p>
                <p className="mt-0.5 text-xs leading-snug text-[#5C5A57]">
                  Toque em <span className="inline-flex items-center gap-1 font-medium"><Share size={12} /> Compartilhar</span> e depois em
                  <span className="font-medium"> "Adicionar à Tela de Início"</span>.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-[#111]">Instalar o aplicativo</p>
                <p className="mt-0.5 text-xs leading-snug text-[#5C5A57]">
                  Adicione o Banco das Revendedoras à tela inicial para acesso rápido.
                </p>
                <button
                  data-testid="install-app-button"
                  onClick={install}
                  className="mt-2 inline-flex items-center gap-2 bg-[#111] px-4 py-2 text-xs uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#333]"
                >
                  <Download size={14} /> Instalar app
                </button>
              </>
            )}
          </div>
          <button
            data-testid="install-app-dismiss"
            onClick={dismiss}
            aria-label="Fechar"
            className="shrink-0 p-1 text-[#5C5A57] transition-colors hover:text-[#111]"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
